import React, { useEffect, useRef, useCallback } from 'react';
import ExploreCard from './ExploreCard';
import { ExploreGridProps, PlaceItem } from '../types/explore';

const ExploreGrid: React.FC<ExploreGridProps> = ({
  items,
  loading,
  hasMore,
  onLoadMore,
  onClickItem,
  onDoubleClickItem,
  onHoverItem,
  onTouchItem,
  touchedPlaceId,
  onOverlayClick,
  variant = 'type',
  showDistance = false,
  pageSize = 10
}) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef<HTMLDivElement | null>(null);

  // Intersection Observer for infinite scroll
  const lastItemElementRef = useCallback((node: HTMLDivElement | null) => {
    if (loading || !hasMore) return; // Don't load more if already loading or no more items
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        onLoadMore();
      }
    }, {
      threshold: 0.1, // Trigger when 10% of the element is visible
      rootMargin: '100px' // Start loading 100px before the element comes into view
    });
    
    if (node) observerRef.current.observe(node);
  }, [loading, hasMore, onLoadMore]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="animate-pulse bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="aspect-square bg-gray-200"></div>
      <div className="p-3">
        <div className="h-4 bg-gray-200 rounded mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-2/3 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
    </div>
  );

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="col-span-full flex justify-center items-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      <span className="ml-2 text-gray-600">Loading more places...</span>
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="col-span-full text-center py-12">
      <div className="text-6xl mb-4">🔍</div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">No places found</h3>
      <p className="text-gray-600">Try adjusting your filters or check back later for new places.</p>
    </div>
  );

  // Error state component
  const ErrorState = () => (
    <div className="col-span-full text-center py-12">
      <div className="text-6xl mb-4">⚠️</div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">Something went wrong</h3>
      <p className="text-gray-600 mb-4">We couldn't load the places. Please try again.</p>
      <button
        onClick={onLoadMore}
        className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
      >
        Try Again
      </button>
    </div>
  );

  return (
    <div className="w-full">
      {/* Grid Container */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4 p-3 md:p-4">
        {/* Place Cards */}
        {items.map((place, index) => {
          const isLastItem = index === items.length - 1;
          return (
            <div
              key={place.id}
              ref={isLastItem ? lastItemElementRef : null}
              className="w-full relative"
            >
              <ExploreCard
                place={place}
                onClick={onClickItem}
                onDoubleClick={onDoubleClickItem}
                onHover={onHoverItem}
                onTouch={onTouchItem}
                className="w-full"
                showOverlay={touchedPlaceId === place.id}
                onOverlayClick={onOverlayClick}
              />
            </div>
          );
        })}

        {/* Loading Skeletons (when initially loading) */}
        {loading && items.length === 0 && (
          <>
            {Array.from({ length: pageSize }).map((_, index) => (
              <LoadingSkeleton key={`skeleton-${index}`} />
            ))}
          </>
        )}

        {/* Loading Spinner (when loading more) */}
        {loading && items.length > 0 && <LoadingSpinner />}

        {/* Empty State */}
        {!loading && items.length === 0 && <EmptyState />}
      </div>

      {/* Load More Button (fallback for poor network) */}
      {!loading && items.length > 0 && hasMore && (
        <div className="text-center py-4">
          <button
            onClick={onLoadMore}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg transition-colors"
          >
            Load More Places
          </button>
        </div>
      )}
      
      {/* End of results message */}
      {!loading && items.length > 0 && !hasMore && (
        <div className="text-center py-4 text-gray-500 text-sm">
          You've reached the end of the list
        </div>
      )}

      {/* Performance Info (development only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="text-xs text-gray-400 text-center py-2">
          Loaded {items.length} places | Variant: {variant}
        </div>
      )}
    </div>
  );
};

export default ExploreGrid;
